import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { MapPin, AlertCircle, Phone, Mail, Edit2, User, AlertTriangle, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import AddApprenantDialog from './AddApprenantDialog';
import api from '../services/api';

const FreinBadge = ({ frein }) => {
  const colors = {
    'Mobilité': 'bg-blue-100 text-blue-700',
    'Logement': 'bg-purple-100 text-purple-700',
    'Financier': 'bg-amber-100 text-amber-700',
    'Santé': 'bg-rose-100 text-rose-700',
    'Juridique': 'bg-red-100 text-red-700',
    'Addiction': 'bg-orange-100 text-orange-700',
    'Administratif': 'bg-teal-100 text-teal-700',
    'Transport': 'bg-cyan-100 text-cyan-700',
    'Garde d\'enfants': 'bg-pink-100 text-pink-700',
    'Autres': 'bg-slate-100 text-slate-700'
  };

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-bold ${colors[frein] || 'bg-slate-100 text-slate-700'}`}>
      {frein}
    </span>
  );
};

const ApprenantCard = ({ bénéficiaire, onUpdate, onDelete, userRole }) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!bénéficiaire) return null;

  const freins = Object.keys(bénéficiaire.freins || {});
  const isAdmin = userRole === 'admin';
  const isAbandon = bénéficiaire.statut === 'abandonné';

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/apprenants/${bénéficiaire.id}`);
      toast.success(`${bénéficiaire.prenom} ${bénéficiaire.nom} a été supprimé avec succès`);
      setShowDeleteDialog(false);
      if (onDelete) {
        onDelete(bénéficiaire.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
    <Card className={`bg-white rounded-3xl p-6 shadow-sm ${isAbandon ? 'border-2 border-red-300 bg-red-50' : 'border-slate-200'}`}>
      <CardHeader className="pb-4 flex flex-row items-center justify-between">
        <div className="flex flex-col gap-1">
          <CardTitle
            className="text-2xl font-bold"
            style={{
              fontFamily: 'Nunito, sans-serif',
              ...(isAbandon ? { textDecoration: 'line-through', color: '#dc2626' } : {})
            }}
          >
            {bénéficiaire.prenom} {bénéficiaire.nom}
          </CardTitle>
          {isAbandon && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full w-fit">
              <AlertTriangle className="w-3 h-3" />
              Abandon du dispositif
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <AddApprenantDialog
            editMode={true}
            bénéficiaireToEdit={bénéficiaire}
            onSuccess={onUpdate}
            triggerButton={
              <Button
                data-testid="edit-beneficiaire-btn"
                variant="outline"
                className="rounded-xl"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Modifier
              </Button>
            }
          />
          {isAdmin && (
            <Button
              data-testid="delete-beneficiaire-btn"
              variant="outline"
              className="rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Informations principales */}
          <div className="space-y-2">
            <div className="text-sm font-bold text-slate-500 uppercase tracking-wider">Informations</div>
            <div className="space-y-1">
              <div className="text-sm">
                <span className="font-medium text-slate-700">N° Sécu:</span>{' '}
                <span className="text-slate-900">{bénéficiaire.numero_securite_sociale || '-'}</span>
              </div>
              <div className="text-sm">
                <span className="font-medium text-slate-700">N° France Travail:</span>{' '}
                <span className="text-slate-900">{bénéficiaire.numero_identifiant_france_travail || '-'}</span>
              </div>
              {bénéficiaire.date_naissance && (
                <div className="text-sm">
                  <span className="font-medium text-slate-700">Date de naissance:</span>{' '}
                  <span className="text-slate-900">{bénéficiaire.date_naissance}</span>
                </div>
              )}
              {bénéficiaire.telephone && (
                <div className="text-sm flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-900">{bénéficiaire.telephone}</span>
                </div>
              )}
              {bénéficiaire.email && (
                <div className="text-sm flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-900">{bénéficiaire.email}</span>
                </div>
              )}
              <div className="text-sm flex items-center gap-2">
                <MapPin className="w-4 h-4 text-slate-500" />
                <span className="text-slate-900">{bénéficiaire.zone_geographique}</span>
              </div>
            </div>
          </div>
          
          {/* Situation professionnelle */}
          <div className="space-y-2">
            <div className="text-sm font-bold text-slate-500 uppercase tracking-wider">Situation</div>
            <div className="space-y-1">
              {bénéficiaire.situation_actuelle && (
                <div className="text-sm">
                  <span className="font-medium text-slate-700">Statut:</span>{' '}
                  <span className="text-slate-900">{bénéficiaire.situation_actuelle}</span>
                </div>
              )}
              {bénéficiaire.niveau_etudes && (
                <div className="text-sm">
                  <span className="font-medium text-slate-700">Niveau d'études:</span>{' '}
                  <span className="text-slate-900">{bénéficiaire.niveau_etudes}</span>
                </div>
              )}
              {bénéficiaire.objectif_professionnel && (
                <div className="text-sm">
                  <span className="font-medium text-slate-700">Projet pro:</span>{' '}
                  <span className="text-slate-900">{bénéficiaire.objectif_professionnel}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Contact d'urgence */}
        <div className="border-t pt-4">
          <div className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            Contact d'urgence
          </div>
          {bénéficiaire.contact_urgence_nom ? (
            <div className="space-y-1">
              <div className="text-sm">
                <span className="font-medium text-slate-700">Nom:</span>{' '}
                <span className="text-slate-900">{bénéficiaire.contact_urgence_nom}</span>
                {bénéficiaire.contact_urgence_lien && (
                  <span className="text-slate-500 ml-2">({bénéficiaire.contact_urgence_lien})</span>
                )}
              </div>
              {bénéficiaire.contact_urgence_telephone && (
                <div className="text-sm flex items-center gap-2">
                  <Phone className="w-4 h-4 text-red-500" />
                  <span className="text-slate-900">{bénéficiaire.contact_urgence_telephone}</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-400 italic">Aucun contact d'urgence renseigné</p>
          )}
        </div>

        {/* Freins */}
        {freins.length > 0 && (
          <div className="border-t pt-4">
            <div className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-500" />
              Freins identifiés
            </div>
            <div className="flex flex-wrap gap-2">
              {freins.map((frein) => (
                <FreinBadge key={frein} frein={frein} />
              ))}
            </div>
          </div>
        )}

        {/* Secteurs visés */}
        {(bénéficiaire.secteurs_activite?.length > 0 || bénéficiaire.secteurs_vises?.length > 0) && (
          <div className="border-t pt-4">
            <div className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">
              Secteurs visés
            </div>
            <div className="flex flex-wrap gap-2">
              {(bénéficiaire.secteurs_vises || bénéficiaire.secteurs_activite || []).map((secteur) => (
                <span
                  key={secteur}
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-bold"
                >
                  {secteur === 'Autre' && bénéficiaire.secteur_autre_precision
                    ? `Autre (${bénéficiaire.secteur_autre_precision})`
                    : secteur}
                </span>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>

    {/* Dialog de confirmation de suppression */}
    <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
      <DialogContent className="max-w-md rounded-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-6 h-6" />
            Confirmer la suppression
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Êtes-vous sûr de vouloir supprimer <strong>{bénéficiaire.prenom} {bénéficiaire.nom}</strong> ?
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-sm text-red-800 font-medium mb-2">
              Cette action est irréversible et supprimera également :
            </p>
            <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
              <li>Le compte utilisateur associé</li>
              <li>Le diagnostic de compétences</li>
              <li>Les candidatures et démarches</li>
              <li>Les contrats et documents</li>
              <li>L'historique des participations aux ateliers</li>
            </ul>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowDeleteDialog(false)}
            disabled={deleting}
            className="rounded-xl"
          >
            Annuler
          </Button>
          <Button 
            onClick={handleDelete}
            disabled={deleting}
            className="bg-red-600 hover:bg-red-700 rounded-xl"
            data-testid="confirm-delete-btn"
          >
            {deleting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Suppression...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer définitivement
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ApprenantCard;
